import React from "react";
import "../embed/styles/base.css";
import { createChatClient } from "../lib/chatApi";
import { SimpleSearchProvider, useSimpleSearch } from "../context/SimpleSearchContext";
import { ProductCandidatesGrid } from "../components/ProductCandidatesGrid";
import {
  appendTestSessionId,
  getBackendBaseUrl,
  persistTestConfig,
  persistTestSessionId,
  updateGlobalTestConfig,
} from "../embed/config";
import { useSearchParams } from "react-router-dom";
import { EVENT_LOCALE_SET, events } from "../embed/events";
import { useLocale } from "../context/LocaleContext";
import type { Product } from "../lib/mockData";
import type { ChatClient } from "../lib/chatApi";
import { ProductPageOverlay } from "../components/ProductPageOverlay";

const CATEGORIES = [
  "Belts",
  "Blazers",
  "Boots",
  "Bow ties",
  "Briefcases",
  "Cases",
  "Clothing care",
  "Coats",
  "Cuff links",
  "Cummerbunds",
  "Derbies",
  "Espadrillos",
  "Gloves",
  "Handkerchiefs",
  "Headgear",
  "Jackets",
  "Loafers",
  "Long-sleeved tops",
  "Overshirts",
  "Oxfords",
  "Scarves",
  "Shirts",
  "Shoe Care",
  "Short-sleeved tops",
  "Shoulder",
  "Slippers",
  "Sneakers",
  "Socks_and_underwear",
  "Suits",
  "Sun glasses",
  "Suspenders",
  "Ties",
  "Toilet",
  "Trousers",
  "Vests",
  "Waistcoats",
  "Wallets",
  "Watches",
  "Weekend",
];

type BackendMode = "dev" | "prod";

function DemoProductsInner({ storeId, client }: { storeId: string; client: ChatClient }) {
  const { t } = useLocale();
  const { products, resultsCount, loading, error, hasMore, search, fetchMore } = useSimpleSearch();
  const [activeCategory, setActiveCategory] = React.useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(null);

  const handlePick = (category: string) => {
    setActiveCategory(category);
    setSelectedProduct(null);
    search(category);
  };

  return (
    <div className="w-full min-h-screen p-6" style={{ backgroundColor: "var(--color-background)" }}>
      <div className="max-w-[1100px] mx-auto">
        <div className="mb-4">
          <div className="text-sm font-semibold mb-1">Demo categories</div>
          <div className="text-[12px] text-gray-600">
            Store: <span className="font-mono">{storeId}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((category) => {
            const isActive = category === activeCategory;
            return (
              <button
                key={category}
                type="button"
                onClick={() => handlePick(category)}
                className="px-3 py-2 text-sm rounded border"
                style={{
                  backgroundColor: isActive ? "var(--color-foreground)" : "var(--color-background)",
                  color: isActive ? "var(--color-background)" : "var(--color-foreground)",
                  borderColor: "var(--color-border)",
                }}
              >
                {category}
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          {error && <div className="ww-alert ww-alert-error">{error}</div>}
          {!error && activeCategory && typeof resultsCount === "number" && resultsCount > 0 && (
            <div className="text-[12px] text-gray-600 mb-2">
              {resultsCount} {t("common.hits")}
            </div>
          )}
          <ProductCandidatesGrid
            products={products}
            onProductSelect={(product) => setSelectedProduct(product)}
            hasMore={hasMore}
            isLoadingMore={loading && products.length > 0}
            totalCount={resultsCount}
            onLoadMore={fetchMore}
          />
          {loading && products.length === 0 && (
            <div className="flex justify-center px-8 py-8">
              <div className="px-4 py-2 rounded-3xl inline-flex items-center">
                <span className="dot-spinner" />
                <span className="ml-[12px] text-[12px] font-semibold text-gray-700">
                  {t("common.thinking")}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      {selectedProduct && (
        <ProductPageOverlay
          product={selectedProduct}
          open
          onClose={() => setSelectedProduct(null)}
          storeId={storeId}
          client={client}
        />
      )}
    </div>
  );
}

export default function DemoProducts() {
  const [params] = useSearchParams();
  const storeId = (params.get("store_id") || "").trim();
  const backendParam = params.get("backend");
  const backendMode: BackendMode = backendParam === "prod" ? "prod" : "dev";

  React.useEffect(() => {
    events.emit(EVENT_LOCALE_SET, { language: "sv", market: "se" });
  }, []);

  const getOrigins = React.useCallback((mode: BackendMode) => {
    const PROD = getBackendBaseUrl();
    const DEV = "https://wandway-back.onrender.com";
    const origin = (mode === "prod" ? PROD : DEV).replace(/\/$/, "");
    return {
      initUrl: `${origin}/widget/init`,
      chatBase: `${origin}/chat`,
    };
  }, []);

  const client = React.useMemo(() => {
    if (!storeId) return null;
    const refreshToken = async () => {
      try {
        const { initUrl, chatBase } = getOrigins(backendMode);
        const url = `${initUrl}?store_id=${encodeURIComponent(storeId)}`;
        const urlWithSession = appendTestSessionId(url);
        const res = await fetch(urlWithSession, {
          method: "GET",
          credentials: "omit",
          cache: "no-store",
          mode: "cors",
        });
        if (!res.ok) return null;
        const data = await res.json();
        persistTestSessionId(data.test_session_id);
        persistTestConfig(data.test_config);
        updateGlobalTestConfig({ testSessionId: data.test_session_id, testConfig: data.test_config });
        const base = (data.api_base || "").replace(/\/$/, "");
        const secureBase = base.replace(/^http:\/\//i, "https://");
        const fixedApiBase = /\/chat$/.test(secureBase) ? secureBase : `${secureBase}/chat`;
        const nextToken = data.token as string | undefined;
        if (!nextToken) return null;
        const nextBase = backendMode === "prod" ? fixedApiBase : chatBase;
        return { token: nextToken, apiBase: nextBase };
      } catch {
        return null;
      }
    };

    const { chatBase } = getOrigins(backendMode);
    return createChatClient({ storeId, apiBase: chatBase, refreshToken });
  }, [backendMode, getOrigins, storeId]);

  if (!storeId) {
    return (
      <div className="w-full min-h-screen p-6" style={{ backgroundColor: "var(--color-background)" }}>
        <div className="max-w-[900px] mx-auto">
          <div className="ww-alert ww-alert-error">
            Missing <span className="font-mono">store_id</span> in the URL.
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <SimpleSearchProvider client={client} minimal={false}>
      <DemoProductsInner storeId={storeId} client={client} />
    </SimpleSearchProvider>
  );
}
